import { Subject, type Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/** Shape of every event that flows through the bridge. */
export type ChatEvent = {
  event: string;
  payload?: unknown;
};

/**
 * Lightweight, bidirectional event bus for communication between
 * the chatbot UI layer and the host application.
 *
 * Chat → Host: action-button clicks, user gestures, etc.
 * Host → Chat: external state changes the UI should react to.
 *
 * Built on RxJS so subscribers get a standard Observable API.
 */
export class ChatbotEventBridge {
  private readonly subject = new Subject<ChatEvent>();

  /** Emit an event (from either side). */
  emit(event: string, payload?: unknown): void {
    this.subject.next({ event, payload });
  }

  /** Subscribe to events with a specific name. */
  on<T = unknown>(eventName: string): Observable<T> {
    return this.subject.pipe(
      filter((e) => e.event === eventName),
      map((e) => e.payload as T)
    );
  }

  /** Subscribe to every event on the bridge. */
  all(): Observable<ChatEvent> {
    return this.subject.asObservable();
  }

  /** Complete the underlying subject — all subscriptions are cleaned up. */
  dispose(): void {
    this.subject.complete();
  }
}
